import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, FileText, LogOut, ArrowUpRight, Mail, Users, NotebookPen, Compass, ShoppingBag, BarChart3, Quote } from "lucide-react";
import SEO from "@/components/common/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

export default function Admin() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        posts: null,
        antipoesias: null,
        glosario: null,
        mapa: null,
        orders: null,
        revenue: null,
        analytics: null,
        testimonios_pending: null,
    });

    useEffect(() => {
        Promise.allSettled([
            api.get("/admin/blog"),
            api.get("/admin/antipoesia"),
            api.get("/admin/glosario"),
            api.get("/admin/mapa-inverso/stats"),
            api.get("/admin/orders/stats"),
            api.get("/admin/analytics/overview?days=30"),
            api.get("/admin/testimonios?status=pending&limit=1000"),
        ]).then(([blogRes, antRes, gloRes, mapRes, ordRes, anaRes, tesRes]) => {
            setStats({
                posts: blogRes.status === "fulfilled" ? blogRes.value.data.length : 0,
                antipoesias:
                    antRes.status === "fulfilled" ? antRes.value.data.length : 0,
                glosario:
                    gloRes.status === "fulfilled" ? gloRes.value.data.length : 0,
                mapa:
                    mapRes.status === "fulfilled" ? mapRes.value.data.total_sessions : 0,
                orders: ordRes.status === "fulfilled" ? ordRes.value.data.paid : 0,
                revenue: ordRes.status === "fulfilled" ? ordRes.value.data.revenue_clp_label : "—",
                analytics: anaRes.status === "fulfilled" ? anaRes.value.data.page_views : 0,
                testimonios_pending: tesRes.status === "fulfilled" ? tesRes.value.data.length : 0,
            });
        });
    }, []);

    async function handleLogout() {
        await logout();
        navigate("/login");
    }

    const sections = [
        {
            to: "/admin/blog",
            code: "01",
            title: "Archivo de Cicatrices",
            subtitle: "Blog · entradas, glosario, currículum, mensajes",
            icon: FileText,
            count: stats.posts,
            countLabel: "entradas",
            testid: "admin-card-blog",
        },
        {
            to: "/admin/antipoesia",
            code: "02",
            title: "Antipoesía Terapéutica",
            subtitle: "Fragmentos · 8 secciones temáticas",
            icon: BookOpen,
            count: stats.antipoesias,
            countLabel: "fragmentos",
            testid: "admin-card-antipoesia",
        },
        {
            to: "/admin/glosario",
            code: "03",
            title: "Glosario del Adicto",
            subtitle: "Términos · 24 letras del alfabeto roto",
            icon: NotebookPen,
            count: stats.glosario,
            countLabel: "términos",
            testid: "admin-card-glosario",
        },
        {
            to: "/admin/mapa-inverso",
            code: "04",
            title: "Mapa Inverso",
            subtitle: "Métricas · expedientes con dashboard",
            icon: Compass,
            count: stats.mapa,
            countLabel: "expedientes",
            testid: "admin-card-mapa-inverso",
        },
        {
            to: "/admin/pedidos",
            code: "05",
            title: "Descargas del libro",
            subtitle: "Órdenes · descargas gratis · email de confirmación",
            icon: ShoppingBag,
            count: stats.orders,
            countLabel: "descargas",
            testid: "admin-card-orders",
        },
        {
            to: "/admin/analytics",
            code: "06",
            title: "Analytics & Monitoring",
            subtitle: "Métricas en vivo · embudo · email · top páginas",
            icon: BarChart3,
            count: stats.analytics,
            countLabel: "page views · 30d",
            testid: "admin-card-analytics",
        },
        {
            to: "/admin/testimonios",
            code: "07",
            title: "Testimonios",
            subtitle: "Modera y destaca testimonios públicos del libro",
            icon: Quote,
            count: stats.testimonios_pending,
            countLabel: "pendientes",
            testid: "admin-card-testimonios",
        },
    ];

    return (
        <>
            <SEO title="Panel interno · Admin" description="Panel administrativo" path="/admin" />
            <section className="py-12 md:py-16" data-testid="admin-hub">
                <div className="container-x">
                    {/* Header */}
                    <div className="flex flex-wrap justify-between items-start gap-4 mb-12 pb-8 border-b border-[var(--line)]">
                        <div>
                            <p className="eyebrow mb-3">Departamento interno</p>
                            <h1 className="font-display text-5xl md:text-7xl text-[var(--paper-2)] leading-[0.95]">
                                Panel <span className="text-[var(--danger)]">Admin</span>
                            </h1>
                            <p className="font-mono text-xs text-[var(--ink-muted)] mt-3">
                                Sesión: {user?.email} · Rol: {user?.role}
                            </p>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="btn-brutal btn-brutal-ghost"
                            data-testid="admin-logout"
                        >
                            <LogOut className="w-4 h-4" />
                            Cerrar sesión
                        </button>
                    </div>

                    {/* Cards */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                        {sections.map((s) => {
                            const Icon = s.icon;
                            return (
                                <Link
                                    key={s.to}
                                    to={s.to}
                                    data-testid={s.testid}
                                    className="dossier hover:border-[var(--danger)] transition-colors group block"
                                >
                                    <div className="dossier-header">
                                        <span>
                                            {s.code} · Departamento
                                        </span>
                                        <span className="text-[var(--danger)]">
                                            CRUD
                                        </span>
                                    </div>
                                    <div className="p-8 md:p-10">
                                        <div className="flex items-start justify-between mb-6">
                                            <Icon className="w-10 h-10 text-[var(--danger)]" />
                                            <ArrowUpRight className="w-5 h-5 text-[var(--ink-muted)] group-hover:text-[var(--danger)] transition-colors" />
                                        </div>
                                        <h2 className="font-display text-3xl md:text-4xl text-[var(--paper-2)] mb-2 group-hover:text-[var(--danger)] transition-colors">
                                            {s.title}
                                        </h2>
                                        <p className="font-mono text-xs text-[var(--ink-muted)] mb-6">
                                            {s.subtitle}
                                        </p>
                                        <div className="pt-4 border-t border-dashed border-[var(--ink-dim)] flex items-baseline justify-between">
                                            <span className="font-display text-4xl text-[var(--warning)]">
                                                {s.count === null
                                                    ? "—"
                                                    : s.count}
                                            </span>
                                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)]">
                                                {s.countLabel}
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>

                    {/* Future placeholders */}
                    <div className="mt-12 pt-12 border-t border-[var(--line)]">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)] mb-4">
                            Próximos módulos
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="border border-[var(--line)] p-5 opacity-50">
                                <Mail className="w-5 h-5 text-[var(--ink-muted)] mb-2" />
                                <p className="font-mono text-sm text-[var(--paper)]">
                                    Buzón de contacto
                                </p>
                                <p className="font-mono text-[10px] text-[var(--ink-dim)] mt-1">
                                    Próximamente
                                </p>
                            </div>
                            <div className="border border-[var(--line)] p-5 opacity-50">
                                <Users className="w-5 h-5 text-[var(--ink-muted)] mb-2" />
                                <p className="font-mono text-sm text-[var(--paper)]">
                                    Suscriptores newsletter
                                </p>
                                <p className="font-mono text-[10px] text-[var(--ink-dim)] mt-1">
                                    Próximamente
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
